import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Sparkles,
  X,
} from "lucide-react";
import Toast from "../components/ui/Toast";
import CustomSelect from "../components/ui/CustomSelect";
import { DiffView } from "../utils/textDiff";
import {
  getAiPromptsOverview,
  getAiPromptDetail,
  saveAiPromptVersion,
  getAiPromptVersions,
  previewAiPromptSync,
  syncAiPrompt,
  getAiPromptChangeLog,
  getAiPromptChangeLogEntry,
  revertAiPromptChangeLog,
} from "../services/aiPromptsService";

/** Sidebar placeholder only — no master prompt / sync (section 15 deferred). */
const SPEND_PROJECTION_KEY = "spend_projection";
const CHANGE_LOG_PAGE_SIZE_OPTIONS = [25, 50, 100];

// --- helpers ----------------------------------------------------------------

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const STATUS_PILL = {
  matches: {
    label: "Matches",
    className: "bg-[#e6f4ea] text-[#137333]",
  },
  behind: {
    label: "Behind",
    className: "bg-[#fef3e2] text-[#b06000]",
  },
  custom: {
    label: "Custom",
    className: "bg-[#fef3e2] text-[#b06000]",
  },
  not_set: {
    label: "Not set",
    className: "bg-[#f0efeb] text-[#5c5b57]",
  },
};

const ACTION_PILL = {
  sync: "bg-[#e6f4ea] text-[#137333]",
  tenant_edit: "bg-[#e8f0fe] text-[#1a73e8]",
  reset_to_master: "bg-[#e8f0fe] text-[#1a73e8]",
  new_version: "bg-[#f0efeb] text-[#5c5b57]",
  revert: "bg-[#f0efeb] text-[#5c5b57]",
};

const extractError = (err) =>
  err?.message || err?.errorDetails?.message || "Something went wrong";

const StatusPill = ({ status, label }) => {
  const cfg = STATUS_PILL[status] || STATUS_PILL.not_set;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium ${cfg.className}`}
    >
      {label || cfg.label}
    </span>
  );
};

const ActionPill = ({ action, label }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium ${
      ACTION_PILL[action] || ACTION_PILL.new_version
    }`}
  >
    {label || action}
  </span>
);

const ModalShell = ({ title, onClose, children, footer, wide = false }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-lg shadow-xl border border-[var(--line,#e4e2dc)] w-full ${
        wide ? "max-w-3xl" : "max-w-lg"
      } max-h-[90vh] flex flex-col`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[var(--line,#e4e2dc)]">
        <h2 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-5 py-4 overflow-y-auto flex-1">{children}</div>
      {footer ? (
        <div className="px-5 py-3 border-t border-[var(--line,#e4e2dc)] flex items-center justify-end gap-2">
          {footer}
        </div>
      ) : null}
    </div>
  </div>
);

const btnSecondary =
  "inline-flex items-center justify-center h-[36px] px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed";
const btnPrimary =
  "inline-flex items-center justify-center h-[36px] px-3.5 text-[13px] font-medium text-white bg-[var(--accent,#a51c1c)] border border-[var(--accent,#a51c1c)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed";
const btnLink =
  "text-[13px] font-medium text-[var(--accent,#a51c1c)] hover:underline disabled:opacity-50";

// --- page -------------------------------------------------------------------

const AiPromptsPage = () => {
  const [tab, setTab] = useState("prompts");
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editorText, setEditorText] = useState("");
  const [selectedTenantIds, setSelectedTenantIds] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [changeLog, setChangeLog] = useState({ entries: [], unrevertedCount: 0, total: 0 });
  const [changeLogBadge, setChangeLogBadge] = useState(0);
  const [changeLogFilter, setChangeLogFilter] = useState("");
  const [changeLogLoading, setChangeLogLoading] = useState(false);
  const [changeLogPage, setChangeLogPage] = useState(1);
  const [changeLogPageSize, setChangeLogPageSize] = useState(25);
  const [changeLogPagination, setChangeLogPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 1,
  });

  const [toast, setToast] = useState({
    message: null,
    type: "success",
    actionLabel: null,
    onAction: null,
  });

  // Modals
  const [versionsModal, setVersionsModal] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [tenantDiff, setTenantDiff] = useState(null);
  const [changeLogView, setChangeLogView] = useState(null);
  const [changeLogViewLoading, setChangeLogViewLoading] = useState(false);
  const [revertConfirm, setRevertConfirm] = useState(null);
  const [reverting, setReverting] = useState(false);

  const dirty = detail ? editorText !== (detail.text || "") : false;

  const loadOverview = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setOverviewLoading(true);
    try {
      const res = await getAiPromptsOverview();
      const data = res?.data || res;
      setOverview(data);
      return data;
    } catch (err) {
      setError(extractError(err));
      return null;
    } finally {
      if (!silent) setOverviewLoading(false);
    }
  }, []);

  const loadChangeLog = useCallback(
    async ({
      promptKey = null,
      page = 1,
      pageSize = 25,
      updateBadge = false,
    } = {}) => {
      setChangeLogLoading(true);
      try {
        const res = await getAiPromptChangeLog({
          promptKey: promptKey || null,
          page,
          pageSize,
        });
        const data = res?.data || res;
        const pagination = data?.pagination || {
          page,
          pageSize,
          total: data?.total ?? (data?.entries || []).length,
          totalPages: 1,
        };
        setChangeLog({
          entries: data?.entries || [],
          unrevertedCount: data?.unrevertedCount ?? 0,
          total: pagination.total ?? 0,
        });
        setChangeLogPagination(pagination);
        setChangeLogPage(pagination.page || page);
        // Badge always reflects the unfiltered change-log total.
        if (updateBadge || !promptKey) {
          setChangeLogBadge(pagination.total ?? 0);
        }
      } catch (err) {
        setError(extractError(err));
      } finally {
        setChangeLogLoading(false);
      }
    },
    [],
  );

  const loadDetail = useCallback(async (promptKey) => {
    if (!promptKey) {
      setDetail(null);
      setEditorText("");
      setSelectedTenantIds(new Set());
      return;
    }
    setDetailLoading(true);
    setError(null);
    try {
      const res = await getAiPromptDetail(promptKey);
      const data = res?.data || res;
      setDetail(data);
      setEditorText(data?.text || "");
      setSelectedTenantIds(new Set());
    } catch (err) {
      setError(extractError(err));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await loadOverview();
      if (cancelled || !data) return;
      const first = data.groups?.[0]?.prompts?.[0];
      if (first?.key) setSelectedKey(first.key);
      await loadChangeLog({
        promptKey: null,
        page: 1,
        pageSize: 25,
        updateBadge: true,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOverview, loadChangeLog]);

  useEffect(() => {
    if (!selectedKey) return;
    if (selectedKey === SPEND_PROJECTION_KEY) {
      setDetail(null);
      setEditorText("");
      setSelectedTenantIds(new Set());
      setDetailLoading(false);
      return;
    }
    loadDetail(selectedKey);
  }, [selectedKey, loadDetail]);

  useEffect(() => {
    if (tab !== "change-log") return;
    loadChangeLog({
      promptKey: changeLogFilter || null,
      page: changeLogPage,
      pageSize: changeLogPageSize,
      updateBadge: !changeLogFilter,
    });
  }, [tab, changeLogFilter, changeLogPage, changeLogPageSize, loadChangeLog]);

  const promptOptions = useMemo(() => {
    const opts = [];
    for (const group of overview?.groups || []) {
      for (const p of group.prompts || []) {
        opts.push({ key: p.key, label: p.label, featureLabel: group.label });
      }
    }
    return opts;
  }, [overview]);

  const notMatchingTenants = useMemo(() => {
    if (!detail?.tenants) return [];
    return detail.tenants.filter((t) => !t.matchesCurrent);
  }, [detail]);

  const refreshAfterMutation = useCallback(async () => {
    await loadOverview({ silent: true });
    if (selectedKey && selectedKey !== SPEND_PROJECTION_KEY) {
      await loadDetail(selectedKey);
    }
    if (tab === "change-log") {
      if (changeLogFilter) {
        try {
          const badgeRes = await getAiPromptChangeLog({
            promptKey: null,
            page: 1,
            pageSize: 1,
          });
          const badgeData = badgeRes?.data || badgeRes;
          setChangeLogBadge(
            badgeData?.pagination?.total ?? badgeData?.total ?? 0,
          );
        } catch {
          /* badge refresh is best-effort */
        }
      }
      await loadChangeLog({
        promptKey: changeLogFilter || null,
        page: changeLogPage,
        pageSize: changeLogPageSize,
        updateBadge: !changeLogFilter,
      });
    } else {
      await loadChangeLog({
        promptKey: null,
        page: 1,
        pageSize: changeLogPageSize,
        updateBadge: true,
      });
    }
  }, [
    loadOverview,
    loadDetail,
    selectedKey,
    loadChangeLog,
    tab,
    changeLogFilter,
    changeLogPage,
    changeLogPageSize,
  ]);

  const changeLogTotalPages = changeLogPagination.totalPages || 1;
  const changeLogRangeStart =
    changeLogPagination.total === 0
      ? 0
      : (changeLogPagination.page - 1) * changeLogPagination.pageSize + 1;
  const changeLogRangeEnd = Math.min(
    changeLogPagination.page * changeLogPagination.pageSize,
    changeLogPagination.total,
  );
  const changeLogPageNumbers = useMemo(() => {
    const current = changeLogPagination.page || 1;
    const windowSize = 5;
    let start = Math.max(1, current - Math.floor(windowSize / 2));
    const end = Math.min(changeLogTotalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [changeLogPagination.page, changeLogTotalPages]);

  const changeLogFilterSelectClass =
    "h-[36px] min-w-[240px] border border-[var(--line-strong,#d5d3cc)] rounded-md px-2.5 py-1.5 text-[13px] text-[var(--ink,#1c1c1a)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--accent,#a51c1c)]";

  const handleSave = async () => {
    if (!detail || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      const res = await saveAiPromptVersion(detail.key, editorText);
      const msg =
        res?.message ||
        `Saved as v${res?.data?.version?.version_number || detail.currentVersion + 1}`;
      setToast({ message: msg, type: "success", actionLabel: null, onAction: null });
      await refreshAfterMutation();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!detail) return;
    setEditorText(detail.text || "");
  };

  const openVersionHistory = async () => {
    if (!detail) return;
    setVersionsLoading(true);
    setVersionsModal({ versions: [] });
    try {
      const res = await getAiPromptVersions(detail.key);
      const versions = res?.data?.versions || res?.versions || [];
      setVersionsModal({ versions });
    } catch (err) {
      setVersionsModal(null);
      setError(extractError(err));
    } finally {
      setVersionsLoading(false);
    }
  };

  const restoreVersionText = (text) => {
    setEditorText(text || "");
    setVersionsModal(null);
  };

  const toggleTenant = (tenantId, enabled) => {
    if (!enabled) return;
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  };

  const selectAllNotMatching = () => {
    setSelectedTenantIds(new Set(notMatchingTenants.map((t) => t.tenantId)));
  };

  const openSyncPreview = async () => {
    if (!detail || selectedTenantIds.size === 0) return;
    setError(null);
    try {
      const res = await previewAiPromptSync(detail.key, Array.from(selectedTenantIds));
      const data = res?.data || res;
      setSyncPreview(data);
    } catch (err) {
      setError(extractError(err));
    }
  };

  const confirmSync = async () => {
    if (!detail || !syncPreview) return;
    setSyncing(true);
    setError(null);
    try {
      const tenantIds = syncPreview.tenants.map((t) => t.tenantId);
      const res = await syncAiPrompt(detail.key, tenantIds);
      const data = res?.data || {};
      const changeLogId = data.changeLogId;
      const count = data.tenantCount ?? tenantIds.length;
      const version = data.version ?? detail.currentVersion;
      setSyncPreview(null);
      setToast({
        message: `Synced v${version} to ${count} tenant${count === 1 ? "" : "s"}.`,
        type: "success",
        actionLabel: changeLogId ? "Undo" : null,
        onAction: changeLogId
          ? async () => {
              try {
                await revertAiPromptChangeLog(changeLogId);
                setToast({
                  message: "Sync undone.",
                  type: "success",
                  actionLabel: null,
                  onAction: null,
                });
                await refreshAfterMutation();
              } catch (undoErr) {
                setToast({
                  message: extractError(undoErr),
                  type: "error",
                  actionLabel: null,
                  onAction: null,
                });
              }
            }
          : null,
      });
      await refreshAfterMutation();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSyncing(false);
    }
  };

  const openChangeLogView = async (id) => {
    setChangeLogViewLoading(true);
    setChangeLogView({ loading: true });
    try {
      const res = await getAiPromptChangeLogEntry(id);
      setChangeLogView(res?.data || res);
    } catch (err) {
      setChangeLogView(null);
      setError(extractError(err));
    } finally {
      setChangeLogViewLoading(false);
    }
  };

  const openRevertConfirm = async (entry) => {
    setError(null);
    const warnings = [];
    try {
      const res = await getAiPromptChangeLogEntry(entry.id);
      const full = res?.data || res;

      if (full.action === "new_version" && detail?.key === full.promptKey) {
        if (detail.currentVersion > full.versionNumber) {
          warnings.push(
            `A newer master version (v${detail.currentVersion}) exists. Reverting will restore the previous text as a new version.`,
          );
        }
      }

      if (
        full.action === "sync" ||
        full.action === "tenant_edit" ||
        full.action === "reset_to_master"
      ) {
        let liveTenants = [];
        if (detail?.key === full.promptKey && detail?.tenants) {
          liveTenants = detail.tenants;
        } else {
          try {
            const detailRes = await getAiPromptDetail(full.promptKey);
            liveTenants = detailRes?.data?.tenants || detailRes?.tenants || [];
          } catch {
            liveTenants = [];
          }
        }
        const byId = new Map(liveTenants.map((t) => [Number(t.tenantId), t]));
        for (const t of full.tenants || []) {
          if (t.tenantId == null) continue;
          const live = byId.get(Number(t.tenantId));
          if (!live) continue;
          const liveText = live.text || "";
          if (liveText !== (t.textAfter || "") && liveText !== (t.textBefore || "")) {
            warnings.push(
              `${live.tenantName || t.tenantName}: changed again after this entry. Reverting will overwrite that later change.`,
            );
          }
        }
      }

      setRevertConfirm({ entry: full, warnings });
    } catch (err) {
      setError(extractError(err));
    }
  };

  const confirmRevert = async () => {
    if (!revertConfirm?.entry) return;
    setReverting(true);
    try {
      const res = await revertAiPromptChangeLog(revertConfirm.entry.id);
      const warnings = res?.data?.warnings || [];
      setRevertConfirm(null);
      let msg = "Change reverted.";
      if (warnings.length) {
        msg += ` ${warnings.length} tenant(s) had later changes that were overwritten.`;
      }
      setToast({
        message: msg,
        type: warnings.length ? "warning" : "success",
        actionLabel: null,
        onAction: null,
      });
      await refreshAfterMutation();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="w-full px-5 py-5 pb-14">
      {/* Header */}
      <div className="mb-5">
        <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
          Super Admin
        </p>
        <h1 className="text-[26px] font-semibold text-[var(--ink,#1c1c1a)] mt-0.5 leading-tight">
          AI prompts
        </h1>
        <p className="text-[13px] text-[var(--muted,#6b6a66)] mt-1 max-w-2xl">
          One master version of each prompt. Sync it to tenants, and roll back any change from the
          log.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--line,#e4e2dc)] mb-5">
        <button
          type="button"
          onClick={() => setTab("prompts")}
          className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
            tab === "prompts"
              ? "border-[var(--accent,#a51c1c)] text-[var(--accent,#a51c1c)]"
              : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
          }`}
        >
          Prompts
        </button>
        <button
          type="button"
          onClick={() => setTab("change-log")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
            tab === "change-log"
              ? "border-[var(--accent,#a51c1c)] text-[var(--accent,#a51c1c)]"
              : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
          }`}
        >
          Change log
          {changeLogBadge > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-[var(--accent,#a51c1c)] text-white">
              {changeLogBadge}
            </span>
          ) : null}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-[var(--crit-soft,#fbe6e6)] border border-[var(--accent,#a51c1c)]/25 text-[var(--accent,#a51c1c)] rounded-lg px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {tab === "prompts" && (
        <div className="flex flex-col lg:flex-row gap-5 items-start">
          {/* Left sidebar */}
          <aside className="w-full lg:w-[320px] shrink-0 space-y-3">
            {overviewLoading && !overview ? (
              <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-[#eeece7] rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {(overview?.groups || []).map((group) => (
                  <div
                    key={group.key}
                    className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden"
                  >
                    <div className="px-3.5 py-2.5 border-b border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)]/40">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                        {group.label}
                      </p>
                    </div>
                    <ul className="divide-y divide-[var(--line,#e4e2dc)]">
                      {(group.prompts || []).map((prompt) => {
                        const selected = selectedKey === prompt.key;
                        return (
                          <li key={prompt.key}>
                            <button
                              type="button"
                              onClick={() => setSelectedKey(prompt.key)}
                              className={`w-full text-left px-3.5 py-3 transition-colors ${
                                selected
                                  ? "bg-[#f8eaea] border-l-[3px] border-l-[var(--accent,#a51c1c)]"
                                  : "border-l-[3px] border-l-transparent hover:bg-[var(--sunken,#eeece7)]/50"
                              }`}
                            >
                              <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)] leading-snug">
                                {prompt.label}
                              </p>
                              <p className="mt-1 flex items-center gap-1.5 text-[12px] text-[var(--muted,#6b6a66)]">
                                <span>v{prompt.currentVersion || 0}</span>
                                <span
                                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                    prompt.allMatch ? "bg-[#137333]" : "bg-[#c47a1a]"
                                  }`}
                                  aria-hidden="true"
                                />
                                <span>
                                  {prompt.matchCount} of {prompt.tenantCount} tenants match
                                </span>
                              </p>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}

                {/* Spend Projection — placeholder card only (no sync / versions) */}
                <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden">
                  <div className="px-3.5 py-2.5 border-b border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)]/40">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                      Spend Projection
                    </p>
                  </div>
                  <ul>
                    <li>
                      <button
                        type="button"
                        onClick={() => setSelectedKey(SPEND_PROJECTION_KEY)}
                        className={`w-full text-left px-3.5 py-3 transition-colors ${
                          selectedKey === SPEND_PROJECTION_KEY
                            ? "bg-[#f8eaea] border-l-[3px] border-l-[var(--accent,#a51c1c)]"
                            : "border-l-[3px] border-l-transparent hover:bg-[var(--sunken,#eeece7)]/50"
                        }`}
                      >
                        <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)] leading-snug">
                          Spend Projection prompt
                        </p>
                        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-[var(--muted,#6b6a66)]">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#f0efeb] text-[11px] font-medium text-[var(--muted,#6b6a66)]">
                            Coming soon
                          </span>
                        </p>
                      </button>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </aside>

          {/* Right detail */}
          <section className="flex-1 min-w-0 w-full space-y-4">
            {!selectedKey && (
              <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg p-8 text-center text-[13px] text-[var(--muted,#6b6a66)]">
                Select a prompt to edit the master version.
              </div>
            )}

            {selectedKey === SPEND_PROJECTION_KEY && (
              <div className="relative overflow-hidden bg-white border border-[var(--line,#e4e2dc)] rounded-lg min-h-[420px]">
                <div
                  className="absolute inset-0 opacity-[0.55]"
                  style={{
                    background:
                      "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(165,28,28,0.12), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 80%, rgba(28,40,60,0.08), transparent 50%), linear-gradient(165deg, #faf9f6 0%, #f3f1ec 45%, #eceae4 100%)",
                  }}
                  aria-hidden="true"
                />
                <div
                  className="absolute inset-0 opacity-[0.35]"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(-12deg, transparent, transparent 11px, rgba(0,0,0,0.02) 11px, rgba(0,0,0,0.02) 12px)",
                  }}
                  aria-hidden="true"
                />
                <div className="relative flex flex-col items-center justify-center text-center px-8 py-16 sm:py-20">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 rounded-full bg-[var(--accent,#a51c1c)]/15 blur-xl scale-150" aria-hidden="true" />
                    <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--accent,#a51c1c)]/20 bg-white shadow-[0_8px_30px_rgba(165,28,28,0.12)]">
                      <Sparkles
                        className="h-7 w-7 text-[var(--accent,#a51c1c)] animate-pulse"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--accent,#a51c1c)]">
                    Master prompt · Spend Projection
                  </p>
                  <h2 className="mt-3 text-[28px] sm:text-[34px] font-semibold tracking-tight text-[var(--ink,#1c1c1a)]">
                    Coming soon
                  </h2>
                  <p className="mt-3 max-w-md text-[14px] leading-relaxed text-[var(--muted,#6b6a66)]">
                    The year-end spend projection prompt will live here — one master version,
                    sync to tenants, and full version history. We&apos;re polishing it before
                    it joins the rest of AI prompts.
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-[var(--line-strong,#d5d3cc)] bg-white/80 px-4 py-2 text-[12px] font-medium text-[var(--muted,#6b6a66)] shadow-sm backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--accent,#a51c1c)] opacity-40" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--accent,#a51c1c)]" />
                    </span>
                    In the works — not available for sync yet
                  </div>
                </div>
              </div>
            )}

            {selectedKey &&
              selectedKey !== SPEND_PROJECTION_KEY &&
              detailLoading &&
              !detail && (
              <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg p-6 space-y-3">
                <div className="h-4 w-40 bg-[#eeece7] rounded animate-pulse" />
                <div className="h-6 w-64 bg-[#eeece7] rounded animate-pulse" />
                <div className="h-80 bg-[#eeece7] rounded animate-pulse" />
              </div>
            )}

            {detail && selectedKey !== SPEND_PROJECTION_KEY && (
              <>
                <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                    Master prompt · {detail.featureLabel}
                  </p>
                  <h2 className="text-[18px] font-semibold text-[var(--ink,#1c1c1a)] mt-1">
                    {detail.label}
                  </h2>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--muted,#6b6a66)]">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#f0efeb] text-[var(--ink,#1c1c1a)] font-medium">
                      v{detail.currentVersion}
                    </span>
                    <span>
                      Saved {formatDateTime(detail.savedAt)}
                      {detail.savedBy ? ` by ${detail.savedBy}` : ""}
                    </span>
                    <button type="button" className={btnLink} onClick={openVersionHistory}>
                      Version history
                    </button>
                  </div>

                  <textarea
                    value={editorText}
                    onChange={(e) => setEditorText(e.target.value)}
                    spellCheck={false}
                    className="mt-4 w-full min-h-[320px] max-h-[520px] overflow-y-auto font-mono text-[12px] leading-relaxed text-[var(--ink,#1c1c1a)] border border-[var(--line-strong,#d5d3cc)] rounded-md px-3.5 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--accent,#a51c1c)] resize-y"
                  />

                  {detail.hint ? (
                    <p className="mt-2 text-[12px] text-[var(--muted,#6b6a66)]">{detail.hint}</p>
                  ) : null}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={!dirty || saving}
                      onClick={handleSave}
                    >
                      {saving
                        ? "Saving…"
                        : `Save as v${(detail.currentVersion || 0) + 1}`}
                    </button>
                    {dirty ? (
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={handleDiscard}
                        disabled={saving}
                      >
                        Discard
                      </button>
                    ) : null}
                  </div>

                  <div className="mt-4 flex items-start gap-2 rounded-md border border-[#c2d7f5] bg-[#e8f0fe] px-3.5 py-3 text-[13px] text-[#174ea6]">
                    <span className="mt-0.5 shrink-0" aria-hidden="true">
                      ⓘ
                    </span>
                    <p>
                      Saving a new version does not change any tenant. Use Sync below when
                      you&apos;re ready to push it.
                    </p>
                  </div>
                </div>

                {/* Tenants card */}
                <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden">
                  <div className="px-5 py-4 border-b border-[var(--line,#e4e2dc)] flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                        Tenants
                      </p>
                      <p className="text-[13px] text-[var(--ink,#1c1c1a)] mt-0.5">
                        {detail.matchCount} of {detail.tenantCount} match v
                        {detail.currentVersion}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={selectAllNotMatching}
                        disabled={notMatchingTenants.length === 0}
                      >
                        Select all not matching ({notMatchingTenants.length})
                      </button>
                      <button
                        type="button"
                        className={btnPrimary}
                        onClick={openSyncPreview}
                        disabled={selectedTenantIds.size === 0}
                      >
                        Sync v{detail.currentVersion} to selected
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-[var(--sunken,#eeece7)]/50 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                          <th className="px-4 py-2.5 w-10" />
                          <th className="px-4 py-2.5">Tenant</th>
                          <th className="px-4 py-2.5">Prompt status</th>
                          <th className="px-4 py-2.5 text-right"> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.tenants || []).map((tenant) => {
                          const matching = tenant.matchesCurrent;
                          const checked = selectedTenantIds.has(tenant.tenantId);
                          return (
                            <tr
                              key={tenant.tenantId}
                              className={`border-t border-[var(--line,#e4e2dc)] ${
                                matching ? "bg-[#fafaf8] text-[var(--muted,#6b6a66)]" : ""
                              }`}
                            >
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={matching}
                                  onChange={() => toggleTenant(tenant.tenantId, !matching)}
                                  className="rounded border-[var(--line-strong,#d5d3cc)] text-[var(--accent,#a51c1c)] focus:ring-[var(--accent,#a51c1c)]/30"
                                  aria-label={`Select ${tenant.tenantName}`}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <p
                                  className={`text-[13px] font-medium ${
                                    matching
                                      ? "text-[var(--muted,#6b6a66)]"
                                      : "text-[var(--ink,#1c1c1a)]"
                                  }`}
                                >
                                  {tenant.tenantName}
                                </p>
                                {tenant.organizationCode ? (
                                  <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                                    {tenant.organizationCode}
                                  </p>
                                ) : null}
                              </td>
                              <td className="px-4 py-3">
                                <StatusPill
                                  status={tenant.status}
                                  label={tenant.statusLabel}
                                />
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  className={btnLink}
                                  onClick={() =>
                                    setTenantDiff({
                                      tenantName: tenant.tenantName,
                                      before: tenant.text || "",
                                      after: detail.text || "",
                                    })
                                  }
                                >
                                  View difference
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {(detail.tenants || []).length === 0 && (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-8 text-center text-[13px] text-[var(--muted,#6b6a66)]"
                            >
                              No active tenants.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {tab === "change-log" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)] mb-1">
                Filter
              </p>
              <CustomSelect
                value={changeLogFilter}
                onChange={(e) => {
                  setChangeLogPage(1);
                  setChangeLogFilter(e.target.value);
                }}
                className={changeLogFilterSelectClass}
              >
                <option value="">All prompts</option>
                {promptOptions.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </CustomSelect>
            </div>
          </div>

          <div className="bg-white border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[var(--sunken,#eeece7)]/50 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Who</th>
                    <th className="px-4 py-2.5">Action</th>
                    <th className="px-4 py-2.5">Prompt</th>
                    <th className="px-4 py-2.5">Applies to</th>
                    <th className="px-4 py-2.5 text-right"> </th>
                  </tr>
                </thead>
                <tbody>
                  {changeLogLoading && changeLog.entries.length === 0 ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-t border-[var(--line,#e4e2dc)]">
                        {Array.from({ length: 6 }).map((__, j) => (
                          <td key={j} className="px-4 py-4">
                            <div className="h-3 bg-[#eeece7] rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : changeLog.entries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]"
                      >
                        No changes yet.
                      </td>
                    </tr>
                  ) : (
                    changeLog.entries.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-t border-[var(--line,#e4e2dc)] hover:bg-[var(--sunken,#eeece7)]/30"
                      >
                        <td className="px-4 py-3 text-[13px] text-[var(--ink,#1c1c1a)] whitespace-nowrap">
                          {formatDateTime(entry.doneAt)}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[var(--ink,#1c1c1a)]">
                          {entry.doneBy || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <ActionPill action={entry.action} label={entry.actionLabel} />
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                            {entry.promptLabel}
                          </p>
                          {entry.featureLabel ? (
                            <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                              {entry.featureLabel}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[var(--ink,#1c1c1a)] max-w-[220px]">
                          {(entry.appliesTo || []).join(", ") || "—"}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            className={btnLink}
                            onClick={() => openChangeLogView(entry.id)}
                          >
                            View
                          </button>
                          {entry.canRevert ? (
                            <>
                              <span className="mx-2 text-[var(--line-strong,#d5d3cc)]">·</span>
                              <button
                                type="button"
                                className={btnLink}
                                onClick={() => openRevertConfirm(entry)}
                              >
                                Revert
                              </button>
                            </>
                          ) : entry.revertedAt ? (
                            <span className="ml-3 text-[12px] text-[var(--muted,#6b6a66)]">
                              Reverted {formatDateTime(entry.revertedAt)}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-[var(--line,#e4e2dc)] bg-[var(--sunken2,#fbfaf8)]">
              <div className="flex items-center gap-3 text-[12px] text-[var(--muted,#6b6a66)]">
                <span>
                  {changeLogPagination.total === 0
                    ? "No records"
                    : `Showing ${changeLogRangeStart.toLocaleString()}–${changeLogRangeEnd.toLocaleString()} of ${changeLogPagination.total.toLocaleString()}`}
                </span>
                <CustomSelect
                  value={changeLogPageSize}
                  onChange={(event) => {
                    setChangeLogPage(1);
                    setChangeLogPageSize(Number(event.target.value));
                  }}
                  className="border border-[var(--line-strong,#d5d3cc)] rounded-md px-2 py-1 text-[12px] text-[var(--ink,#1c1c1a)] bg-white focus:outline-none"
                >
                  {CHANGE_LOG_PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} / page
                    </option>
                  ))}
                </CustomSelect>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setChangeLogPage(1)}
                  disabled={changeLogPagination.page <= 1}
                  className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
                  aria-label="First page"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setChangeLogPage((current) => Math.max(1, current - 1))
                  }
                  disabled={changeLogPagination.page <= 1}
                  className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {changeLogPageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setChangeLogPage(pageNumber)}
                    className={`min-w-[30px] px-2 py-1 text-[12px] font-medium rounded-md border transition-colors ${
                      pageNumber === changeLogPagination.page
                        ? "border-[var(--accent,#a51c1c)] bg-[var(--accent,#a51c1c)] text-white"
                        : "border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setChangeLogPage((current) =>
                      Math.min(changeLogTotalPages, current + 1),
                    )
                  }
                  disabled={changeLogPagination.page >= changeLogTotalPages}
                  className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setChangeLogPage(changeLogTotalPages)}
                  disabled={changeLogPagination.page >= changeLogTotalPages}
                  className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
                  aria-label="Last page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version history modal */}
      {versionsModal && (
        <ModalShell
          title="Version history"
          wide
          onClose={() => setVersionsModal(null)}
          footer={
            <button type="button" className={btnSecondary} onClick={() => setVersionsModal(null)}>
              Close
            </button>
          }
        >
          {versionsLoading ? (
            <p className="text-[13px] text-[var(--muted,#6b6a66)]">Loading…</p>
          ) : (versionsModal.versions || []).length === 0 ? (
            <p className="text-[13px] text-[var(--muted,#6b6a66)]">No versions found.</p>
          ) : (
            <div className="space-y-5">
              {versionsModal.versions.map((v) => (
                <div
                  key={v.id}
                  className="border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden"
                >
                  <div className="px-4 py-3 bg-[var(--sunken,#eeece7)]/40 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-semibold text-[var(--ink,#1c1c1a)]">
                        v{v.versionNumber}
                      </span>
                      {v.isCurrent ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#e6f4ea] text-[#137333]">
                          Current master
                        </span>
                      ) : null}
                      <span className="text-[12px] text-[var(--muted,#6b6a66)]">
                        {formatDateTime(v.createdAt)}
                        {v.createdBy ? ` · ${v.createdBy}` : ""}
                      </span>
                    </div>
                    {!v.isCurrent ? (
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={() => restoreVersionText(v.text)}
                      >
                        Restore as v{(detail?.currentVersion || 0) + 1}
                      </button>
                    ) : null}
                  </div>
                  <div className="px-4 py-3 max-h-48 overflow-y-auto">
                    {v.previousText != null ? (
                      <DiffView before={v.previousText} after={v.text} />
                    ) : (
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-gray-800">
                        {v.text}
                      </pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalShell>
      )}

      {/* Sync preview modal */}
      {syncPreview && (
        <ModalShell
          title={`Sync v${syncPreview.currentVersion} — preview`}
          wide
          onClose={() => setSyncPreview(null)}
          footer={
            <>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => setSyncPreview(null)}
                disabled={syncing}
              >
                Cancel
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={confirmSync}
                disabled={syncing}
              >
                {syncing ? "Syncing…" : "Confirm sync"}
              </button>
            </>
          }
        >
          {syncPreview.hasCustom ? (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-[#f0c36d] bg-[#fef3e2] px-3.5 py-3 text-[13px] text-[#8a5a00]">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <p>
                One or more selected tenants have a custom prompt. Syncing will overwrite their
                custom text with the master version.
              </p>
            </div>
          ) : null}
          <div className="space-y-4">
            {(syncPreview.tenants || []).map((t) => (
              <div
                key={t.tenantId}
                className="border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden"
              >
                <div className="px-4 py-2.5 bg-[var(--sunken,#eeece7)]/40 flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                      {t.tenantName}
                    </p>
                    {t.organizationCode ? (
                      <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                        {t.organizationCode}
                      </p>
                    ) : null}
                  </div>
                  <StatusPill status={t.status} label={t.statusLabel} />
                </div>
                <div className="px-4 py-3 max-h-40 overflow-y-auto">
                  <DiffView before={t.textBefore || ""} after={t.textAfter || ""} />
                </div>
              </div>
            ))}
          </div>
        </ModalShell>
      )}

      {/* Tenant difference modal */}
      {tenantDiff && (
        <ModalShell
          title={`Difference — ${tenantDiff.tenantName}`}
          wide
          onClose={() => setTenantDiff(null)}
          footer={
            <button type="button" className={btnSecondary} onClick={() => setTenantDiff(null)}>
              Close
            </button>
          }
        >
          <p className="text-[12px] text-[var(--muted,#6b6a66)] mb-3">
            Red = tenant text removed · Green = master text added
          </p>
          <div className="border border-[var(--line,#e4e2dc)] rounded-lg p-3 max-h-[60vh] overflow-y-auto">
            <DiffView before={tenantDiff.before} after={tenantDiff.after} />
          </div>
        </ModalShell>
      )}

      {/* Change log view modal */}
      {changeLogView && (
        <ModalShell
          title={
            changeLogViewLoading || changeLogView.loading
              ? "Change details"
              : `${changeLogView.actionLabel || "Change"} — ${changeLogView.promptLabel || ""}`
          }
          wide
          onClose={() => setChangeLogView(null)}
          footer={
            <button type="button" className={btnSecondary} onClick={() => setChangeLogView(null)}>
              Close
            </button>
          }
        >
          {changeLogViewLoading || changeLogView.loading ? (
            <p className="text-[13px] text-[var(--muted,#6b6a66)]">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-[13px]">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                    When
                  </p>
                  <p>{formatDateTime(changeLogView.doneAt)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                    Who
                  </p>
                  <p>{changeLogView.doneBy || "—"}</p>
                </div>
              </div>
              {(changeLogView.tenants || []).length === 0 ? (
                <p className="text-[13px] text-[var(--muted,#6b6a66)]">No tenant diffs recorded.</p>
              ) : (
                (changeLogView.tenants || []).map((t, idx) => (
                  <div
                    key={`${t.tenantId ?? "master"}-${idx}`}
                    className="border border-[var(--line,#e4e2dc)] rounded-lg overflow-hidden"
                  >
                    <div className="px-4 py-2.5 bg-[var(--sunken,#eeece7)]/40">
                      <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                        {t.tenantName || (t.tenantId == null ? "Master" : "Tenant")}
                      </p>
                    </div>
                    <div className="px-4 py-3 max-h-40 overflow-y-auto">
                      <DiffView before={t.textBefore || ""} after={t.textAfter || ""} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </ModalShell>
      )}

      {/* Revert confirm modal */}
      {revertConfirm && (
        <ModalShell
          title="Revert this change?"
          onClose={() => !reverting && setRevertConfirm(null)}
          footer={
            <>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => setRevertConfirm(null)}
                disabled={reverting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={confirmRevert}
                disabled={reverting}
              >
                {reverting ? "Reverting…" : "Revert"}
              </button>
            </>
          }
        >
          <p className="text-[13px] text-[var(--ink,#1c1c1a)]">
            This will undo{" "}
            <span className="font-medium">
              {revertConfirm.entry.actionLabel || revertConfirm.entry.action}
            </span>{" "}
            on{" "}
            <span className="font-medium">{revertConfirm.entry.promptLabel}</span>.
          </p>
          {(revertConfirm.warnings || []).length > 0 ? (
            <div className="mt-3 space-y-2">
              {revertConfirm.warnings.map((w, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-[#f0c36d] bg-[#fef3e2] px-3.5 py-3 text-[13px] text-[#8a5a00]"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <p>{w}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-[13px] text-[var(--muted,#6b6a66)]">
              Affected tenants will be restored to their previous prompt text.
            </p>
          )}
        </ModalShell>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        actionLabel={toast.actionLabel}
        onAction={toast.onAction}
        onClose={() =>
          setToast({ message: null, type: "success", actionLabel: null, onAction: null })
        }
        duration={12000}
      />
    </div>
  );
};

export default AiPromptsPage;
